import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ClassifyResponse, TagEditSiteGroup, BatchUploadPhotoItem, SitePhotoItem } from '../api/photoApi';
import type { PhotoDetailContext } from '../main/PhotoDetailMain';
import type { ShareSelectedPhoto, ShareSiteInfo } from '../types/photo';

// 분류 화면 → 태그 화면 → 이전 버튼으로 복귀 시 현장 카드 복원용
export interface ClassifySiteGroupSnapshot {
  siteId: number;
  siteName: string;
  address: string | null;
  customerName: string | null;
  siteStatus: string | null;
  siteStatusName: string | null;
  newCount: number;
  prevCount: number;
  thumbPhotos: SitePhotoItem[];
  totalOnSite: number;
  sessionPhotoIds: number[];
}

interface PhotoNavState {
  // upload → classify-site
  fromUploadFlow: boolean;
  classifyResult: ClassifyResponse | null;
  currentSessionId: number | null;
  batchPhotos: BatchUploadPhotoItem[];
  // classify-site → tag-bulk-edit
  tagEditGroups: TagEditSiteGroup[];
  // tag-bulk-edit 세션 중 등록된 태그 (이전 버튼 후 재진입 시 복원용)
  tagBulkEditSavedTags: Record<number, string[]>;
  // 분류 화면 현장 카드 스냅샷 (태그 화면 이전 버튼 복귀 시 복원용)
  classifySiteGroups: ClassifySiteGroupSnapshot[] | null;
  // site-detail → tag-bulk-edit 진입 시 설정 — 이전/완료 버튼이 업로드 흐름 대신 이 경로로 복귀
  tagEditReturnPath: string | null;
  // site-detail/search → photo-detail
  photoDetailCtx: PhotoDetailContext | null;
  // site-list "미분류 사진" 카드 → classify-site 진입 시 전체 현장 목록을 처음부터 펼쳐서 보여줄지(1회성 신호)
  classifyShowAllSitesOnEntry: boolean;
  // share
  sharePhotos: ShareSelectedPhoto[];
  shareSite: ShareSiteInfo | null;
  shareFromPage: 'share' | 'site-detail';
  shareScope: 'site' | 'photos';
  preSelectShare: boolean;

  setUploadResult: (result: ClassifyResponse, sessionId: number, photos: BatchUploadPhotoItem[]) => void;
  clearUploadResult: () => void;
  setTagEditGroups: (groups: TagEditSiteGroup[]) => void;
  setTagBulkEditSavedTags: (tags: Record<number, string[]>) => void;
  setClassifySiteGroups: (groups: ClassifySiteGroupSnapshot[]) => void;
  clearClassifySiteGroups: () => void;
  setTagEditReturnPath: (path: string | null) => void;
  setPhotoDetailCtx: (ctx: PhotoDetailContext) => void;
  setClassifyShowAllSitesOnEntry: (v: boolean) => void;
  setShareSend: (photos: ShareSelectedPhoto[], site: ShareSiteInfo | null, from: 'share' | 'site-detail', scope: 'site' | 'photos') => void;
  setPreSelectShare: (v: boolean) => void;
}

export const usePhotoNavStore = create<PhotoNavState>()(
  persist(
    (set) => ({
      fromUploadFlow: false,
      classifyResult: null,
      currentSessionId: null,
      batchPhotos: [],
      tagEditGroups: [],
      tagBulkEditSavedTags: {},
      classifySiteGroups: null,
      tagEditReturnPath: null,
      photoDetailCtx: null,
      classifyShowAllSitesOnEntry: false,
      sharePhotos: [],
      shareSite: null,
      shareFromPage: 'share' as const,
      shareScope: 'photos' as const,
      preSelectShare: false,

      setUploadResult: (result, sessionId, photos) =>
        set({ fromUploadFlow: true, classifyResult: result, currentSessionId: sessionId, batchPhotos: photos }),
      clearUploadResult: () =>
        set({ fromUploadFlow: false, classifyResult: null, currentSessionId: null, batchPhotos: [] }),
      setTagEditGroups: (groups) => set({ tagEditGroups: groups }),
      setTagBulkEditSavedTags: (tags) => set({ tagBulkEditSavedTags: tags }),
      setClassifySiteGroups: (groups) => set({ classifySiteGroups: groups }),
      clearClassifySiteGroups: () => set({ classifySiteGroups: null }),
      setTagEditReturnPath: (path) => set({ tagEditReturnPath: path }),
      setPhotoDetailCtx: (ctx) => set({ photoDetailCtx: ctx }),
      setClassifyShowAllSitesOnEntry: (v) => set({ classifyShowAllSitesOnEntry: v }),
      setShareSend: (photos, site, from, scope) =>
        set({ sharePhotos: photos, shareSite: site, shareFromPage: from, shareScope: scope, preSelectShare: false }),
      setPreSelectShare: (v) => set({ preSelectShare: v }),
    }),
    {
      name: 'photo-nav-store',
      partialize: (state) => ({
        fromUploadFlow: state.fromUploadFlow,
        classifyResult: state.classifyResult,
        currentSessionId: state.currentSessionId,
        batchPhotos: state.batchPhotos,
        photoDetailCtx: state.photoDetailCtx,
        sharePhotos: state.sharePhotos,
        shareSite: state.shareSite,
        shareFromPage: state.shareFromPage,
        shareScope: state.shareScope,
        preSelectShare: state.preSelectShare,
      }),
    },
  ),
);
